import Request from '../services/Request'

/**
 * PermissionsRepository
 *
 * Gère tous les appels API liés au système RBAC :
 *   - getRoles()                          → GET  /rbac/roles
 *   - updateRolePermissions(uuid, perms)  → PUT  /rbac/roles/{uuid}/permissions
 *   - getMyPermissions()                  → GET  /rbac/me/permissions
 *   - updateMyPermissions(overrides)      → PUT  /rbac/me/permissions
 */
export default class PermissionsRepository {

    /**
     * Récupère tous les rôles avec leurs permissions (admin uniquement).
     *
     * @returns {Promise<{
     *   roles: Array<{id: string, name: string, permissions: string[]}>,
     *   permissions: Object<string, Array<{id: string, name: string, group: string}>>,
     *   user_customizable_permissions: string[]
     * }>}
     */
    static getRoles () {
        return new Promise((resolve, reject) => {
            Request
                .make('GET', '/rbac/roles')
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error))
        })
    }

    /**
     * Récupère les 10 derniers logs de modification de la matrice.
     */
    static getRbacLogs () {
        return new Promise((resolve, reject) => {
            Request
                .make('GET', '/rbac/logs')
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error))
        })
    }

    /**
     * Met à jour les permissions d'un rôle (admin uniquement).
     *
     * @param {string}   roleUuid    UUID du rôle
     * @param {string[]} permissions Tableau de slugs de permissions (ex: ['ratings.view', 'users.create'])
     * @returns {Promise<{id: string, name: string, permissions: string[]}>}
     */
    static updateRolePermissions (roleUuid, permissions) {
        return new Promise((resolve, reject) => {
            Request
                .make('PUT', `/rbac/roles/${roleUuid}/permissions`, { permissions })
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error))
        })
    }

    /**
     * Récupère les permissions effectives de l'utilisateur connecté.
     *
     * @returns {Promise<{
     *   permissions: string[],
     *   permission_overrides: string[],
     *   user_customizable_permissions: Array<{
     *     name: string,
     *     is_active: boolean,
     *     source: 'role'|'override'|'none',
     *     via_role: boolean,
     *     has_override: boolean
     *   }>
     * }>}
     */
    static getMyPermissions () {
        return new Promise((resolve, reject) => {
            Request
                .make('GET', '/rbac/me/permissions')
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error))
        })
    }

    /**
     * Met à jour les permissions personnalisables de l'utilisateur connecté.
     *
     * @param {Object<string, boolean>} overrides Objet { 'ratings.export': true, 'notifications.manage': false }
     * @returns {Promise<{
     *   permissions: string[],
     *   permission_overrides: string[],
     *   user_customizable_permissions: Array
     * }>}
     */
    static updateMyPermissions (overrides) {
        return new Promise((resolve, reject) => {
            Request
                .make('PUT', '/rbac/me/permissions', { permissions: overrides })
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error))
        })
    }

    // =========================================================================
    // CRUD DES ROLES (super_admin uniquement)
    // =========================================================================

    /**
     * Liste tous les rôles avec le nombre d'utilisateurs assignés.
     */
    static listRoles () {
        return new Promise((resolve, reject) => {
            Request
                .make('GET', '/rbac/roles/manage')
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error))
        })
    }

    /**
     * Crée un nouveau rôle.
     * @param {string} name - Nom du rôle (minuscules, chiffres, underscores)
     */
    static createRole (name) {
        return new Promise((resolve, reject) => {
            Request
                .make('POST', '/rbac/roles/manage', { name })
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error))
        })
    }

    /**
     * Renomme un rôle existant.
     * @param {string} roleUuid - UUID du rôle
     * @param {string} name     - Nouveau nom du rôle
     */
    static updateRole (roleUuid, name) {
        return new Promise((resolve, reject) => {
            Request
                .make('PUT', `/rbac/roles/manage/${roleUuid}`, { name })
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error))
        })
    }

    /**
     * Supprime un rôle.
     * @param {string} roleUuid - UUID du rôle
     */
    static deleteRole (roleUuid) {
        return new Promise((resolve, reject) => {
            Request
                .make('DELETE', `/rbac/roles/manage/${roleUuid}`)
                .then(({ data }) => resolve(data))
                .catch((error) => reject(error))
        })
    }
}
